import { useQuery } from "convex/react";

import { Badge } from "@/components/ui/badge";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import type { ClioMatterContactEntry } from "./types";

interface MatterContactsProps {
	connectionId: Id<"oauthConnections">;
	clioMatterId: number;
}

function ContactCard({ entry }: { entry: ClioMatterContactEntry }) {
	const c = entry.contact;
	return (
		<div className="flex flex-col gap-2 rounded-md border p-3">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 flex-col">
					<div className="flex items-center gap-2">
						<span className="truncate font-medium text-sm">
							{c?.name ?? `Contact ${entry.contactClioId}`}
						</span>
						{c?.type && (
							<Badge variant="outline" className="font-normal">
								{c.type}
							</Badge>
						)}
					</div>
					{c?.primaryEmail && (
						<span className="truncate text-xs text-muted-foreground">
							{c.primaryEmail}
						</span>
					)}
					{c?.primaryPhone && (
						<span className="truncate text-xs text-muted-foreground">
							{c.primaryPhone}
						</span>
					)}
				</div>
				<div className="flex flex-col items-end gap-1">
					{entry.isClient && <Badge>Client</Badge>}
					{entry.relationship && (
						<Badge variant="outline">{entry.relationship}</Badge>
					)}
				</div>
			</div>
			{c?.addresses && c.addresses.length > 0 && (
				<div className="text-xs text-muted-foreground">
					{c.addresses[0].street ?? ""} {c.addresses[0].city ?? ""}{" "}
					{c.addresses[0].province ?? ""} {c.addresses[0].postalCode ?? ""}
				</div>
			)}
		</div>
	);
}

export function MatterContacts({
	connectionId,
	clioMatterId,
}: MatterContactsProps) {
	const entries = useQuery(api.clio.queries.listMatterContacts, {
		connectionId,
		clioMatterId,
	});

	if (entries === undefined) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>Loading contacts…</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}
	if (entries.length === 0) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>No contacts on this matter.</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}

	const clients = entries.filter((e) => e.isClient);
	const related = entries.filter((e) => !e.isClient);

	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="flex flex-col gap-4 p-4">
				{clients.length > 0 && (
					<section>
						<h3 className="mb-2 text-sm font-semibold">
							Clients ({clients.length})
						</h3>
						<div className="flex flex-col gap-2">
							{clients.map((entry) => (
								<ContactCard key={entry._id} entry={entry} />
							))}
						</div>
					</section>
				)}
				{clients.length > 0 && related.length > 0 && <Separator />}
				{related.length > 0 && (
					<section>
						<h3 className="mb-2 text-sm font-semibold">
							Related contacts ({related.length})
						</h3>
						<div className="flex flex-col gap-2">
							{related.map((entry) => (
								<ContactCard key={entry._id} entry={entry} />
							))}
						</div>
					</section>
				)}
			</div>
		</ScrollArea>
	);
}
